import React, { useCallback, useEffect, useMemo, useRef } from "react";
import {
  Typography as MuiTypography,
  Divider as MuiDivider,
  CircularProgress,
} from "@material-ui/core";
import styled from "styled-components/macro";
import { spacing } from "@material-ui/system";
import { Helmet } from "react-helmet-async";
import { z } from "zod";

import useQueryParams from "../../hooks/useQueryParams";
import ScorecardTable from "../../components/scorecard/ScorecardTable";
import ScorecardLegend from "../../components/scorecard/ScorecardLegend";
import ScorecardHowToRead from "../../components/scorecard/ScorecardHowToRead";
import ScorecardPeriodControls from "../../components/scorecard/ScorecardPeriodControls";
import ScorecardLiveDateControls from "../../components/scorecard/ScorecardLiveDateControls";
import useWqScorecard, {
  DEFAULT_SCORECARD_KEY,
  LIVE_PERIOD_KEY,
  rebuildLivePeriod,
} from "./useWqScorecard";
import { buildMapFlyToLink, buildMapLink } from "./scorecardLinks";
import {
  buildScorecardCsv,
  buildScorecardCsvFilename,
} from "./buildScorecardCsv";
import { downloadCsv } from "../../utils";
import { useReactToPrint } from "react-to-print";
import {
  PRINT_INTRO_CLASS,
  PRINT_ONLY_CLASS,
  PRINT_PAGE_STYLE,
  PRINT_ROOT_CLASS,
  PRINT_TABLE_CLASS,
} from "./scorecardPrint";
import {
  DISCLAIMER,
  EMPTY_STATE,
  PAGE_INTRO,
  PAGE_TITLE,
} from "./scorecardCopy";

const Section = styled.section`
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
`;
const Typography = styled(MuiTypography)(spacing);
const Divider = styled(MuiDivider)(spacing);

const Disclaimer = styled(MuiTypography)`
  font-style: italic;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #ddd;
`;

// Hidden on screen, shown in the printout: a sheet of colored cells means
// nothing without the period and flow condition it describes.
const PrintHeader = styled.div`
  display: none;
  margin-bottom: 8px;

  h2 {
    margin: 0 0 2px;
    font-size: 12pt;
  }

  p {
    margin: 0;
    font-size: 8pt;
  }
`;

// The printed sheet wants the legend on the cover page, before the grid, but
// on screen it belongs under the table where people expect it. Both orders come
// from one DOM by flexing the container and setting order explicitly here for
// screen; scorecardPrint.js flips it for paper.
const PrintRoot = styled.div`
  display: flex;
  flex-direction: column;

  > .${PRINT_TABLE_CLASS} {
    order: 1;
  }

  > .${PRINT_INTRO_CLASS} {
    order: 2;
  }
`;

const Centered = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px;
`;

/* ------ URL state ------------------------------------------------------- */
const DEFAULTS = {
  flow: "alldata",
  statistic: "median",
};

const schema = z.object({
  // period is left free-form: the valid keys come from the API, and an unknown
  // one falls back to the default below rather than failing the whole parse.
  period: z.string().optional(),
  flow: z.enum(["alldata", "baseflow", "stormflow"]).default(DEFAULTS.flow),
  statistic: z.enum(["median", "pctile"]).default(DEFAULTS.statistic),
  // The reservoir rows are on unless the URL says otherwise; only "off" is
  // ever written to the URL, so the default address stays short.
  reservoir: z
    .preprocess(
      (v) =>
        v === "0" || v === "false"
          ? false
          : v === "1" || v === "true"
          ? true
          : v,
      z.boolean()
    )
    .default(true),
  // Absent means every column, which keeps the default URL short.
  params: z
    .preprocess(
      (v) =>
        typeof v === "string"
          ? v
              .split(",")
              .map(Number)
              .filter((n) => Number.isInteger(n))
          : v,
      z.array(z.number())
    )
    .optional(),
});

export default function WqScorecard() {
  const { params, setParams } = useQueryParams(schema);

  const {
    table,
    periods,
    livePeriod,
    selectedPeriod,
    benchmarkColors,
    benchmarkLineByParameter,
    isLoading,
    error,
    refetchStats,
    refetchPeriods,
  } = useWqScorecard({
    scorecardKey: DEFAULT_SCORECARD_KEY,
    includeReservoir: params.reservoir,
    period: params.period,
    flow: params.flow,
    statistic: params.statistic,
    parameterIds: params.params,
  });

  // Once the period list arrives, settle on one: whatever the URL asked for if
  // it exists, otherwise Live.
  //
  // Live is the default because it is the window the rest of the site is on.
  // Opening here agrees with the interactive map, the date pickers below show
  // the window actually in force, and the per-cell map links are available —
  // all three of which are only true on Live. The is_default flag in the
  // database is the fallback for when Live cannot be offered.
  useEffect(() => {
    if (!periods.length) return;
    const known = periods.some((p) => p.period_key === params.period);
    if (known) return;
    const fallback =
      periods.find((p) => p.period_key === LIVE_PERIOD_KEY) ??
      periods.find((p) => p.is_default) ??
      periods[0];
    if (fallback) setParams({ ...params, period: fallback.period_key });
  }, [periods, params, setParams]);

  // FiltersForm hands back an updater function, the chips hand back a plain
  // patch. Accept both, and collapse a full parameter selection to null so the
  // default case leaves no `params` in the URL.
  const allParameterIds = useMemo(
    () => table.allParameters.map((p) => p.parameterid),
    [table.allParameters]
  );

  const handleControlChange = useCallback(
    (update) =>
      setParams((prev) => {
        const next =
          typeof update === "function" ? update(prev) : { ...prev, ...update };
        // Only the non-default reservoir state is written to the URL.
        const reservoir = next.reservoir === false ? false : null;
        if (
          Array.isArray(next.params) &&
          allParameterIds.length > 0 &&
          next.params.length === allParameterIds.length
        ) {
          return { ...next, params: null, reservoir };
        }
        return { ...next, reservoir };
      }),
    [setParams, allParameterIds]
  );

  // The map's recalculation has finished. Rebuild the scorecard's Live period
  // over the new window, then move the page there, because the numbers that
  // changed are the Live ones. A failed rebuild still switches the period, so
  // the reader sees the stale Live build rather than a snapshot that did not
  // move; the periods list carries its last_built stamp.
  const handleWindowApplied = useCallback(async () => {
    try {
      await rebuildLivePeriod();
    } catch (err) {
      console.error("Live scorecard rebuild failed", err);
    }
    setParams({ ...params, period: LIVE_PERIOD_KEY });
    refetchPeriods();
    refetchStats();
  }, [params, setParams, refetchStats, refetchPeriods]);

  const isLive = params.period === LIVE_PERIOD_KEY;

  // Graph mode always renders the site-wide analysis window, so a link from a
  // snapshot period could land on a map that contradicts the cell it came from:
  // a red cell under the full record opening a green dot under the live window.
  // The link is therefore offered on Live only, where the two agree by
  // construction.
  const getCellHref = useCallback(
    (cell) =>
      isLive && cell && cell.hasData
        ? buildMapLink({
            locationid: cell.locationid,
            parameterid: cell.parameterid,
            flow: params.flow,
            statistic: params.statistic,
          })
        : null,
    [isLive, params.flow, params.statistic]
  );

  // Explore mode cannot color by water quality, so centring the map on a site
  // is safe from any period. Available on every row, always.
  const getRowHref = useCallback(
    (row) => buildMapFlyToLink({ lat: row.lat, lon: row.lon }),
    []
  );

  // Exports exactly what is on screen: the chosen period, flow, statistic and
  // parameter columns, with the window recorded in a header block.
  const handleDownloadCsv = useCallback(() => {
    downloadCsv(
      buildScorecardCsvFilename({
        periodKey: params.period,
        flow: params.flow,
        statistic: params.statistic,
      }),
      buildScorecardCsv({
        table,
        period: selectedPeriod,
        flowLabel: table.flowLabel,
        statisticLabel: table.statisticLabel,
        scorecardLabel: params.reservoir
          ? "Watershed and Reservoir Scorecard"
          : "Watershed Plan Scorecard",
      })
    );
  }, [
    table,
    selectedPeriod,
    params.period,
    params.flow,
    params.statistic,
    params.reservoir,
  ]);

  // Print the table and legend alone, landscape, aiming at one sheet. The
  // controls, map links and action buttons are suppressed; see scorecardPrint.
  const printRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `${PAGE_TITLE} - ${selectedPeriod?.period_label ?? ""}`,
    pageStyle: PRINT_PAGE_STYLE,
  });

  const printSummary = [
    selectedPeriod?.period_label,
    selectedPeriod?.stats_start && selectedPeriod?.stats_end
      ? `${selectedPeriod.stats_start} to ${selectedPeriod.stats_end}`
      : null,
    table.flowLabel,
    table.statisticLabel,
  ]
    .filter(Boolean)
    .join(" \u00b7 ");

  return (
    <>
      <Helmet title={PAGE_TITLE} />
      <header>
        <Typography variant="h3" component="h1" gutterBottom>
          {PAGE_TITLE}
        </Typography>
        <Divider my={4} />
      </header>

      <main>
        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            About this Page
          </Typography>
          <Typography variant="body1" gutterBottom>
            {PAGE_INTRO}
          </Typography>
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Filters
          </Typography>
          <ScorecardPeriodControls
            periods={periods}
            period={params.period}
            flow={params.flow}
            statistic={params.statistic}
            statisticLabel={table.statisticLabel}
            allParameters={table.allParameters}
            selectedParameters={params.params}
            isLive={isLive}
            includeReservoir={params.reservoir}
            showReservoirToggle
            onChange={handleControlChange}
            disabled={isLoading}
          />

          <ScorecardLiveDateControls
            periodStart={selectedPeriod?.stats_start}
            periodEnd={selectedPeriod?.stats_end}
            liveStart={livePeriod?.stats_start}
            liveEnd={livePeriod?.stats_end}
            onApplied={handleWindowApplied}
            disabled={isLoading}
          />
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            Scorecard
          </Typography>
          {error ? (
            <Typography color="error">
              {`Could not load the scorecard: ${error.message}`}
            </Typography>
          ) : isLoading ? (
            <Centered>
              <CircularProgress aria-label="Loading scorecard" />
            </Centered>
          ) : (
            <PrintRoot ref={printRef} className={PRINT_ROOT_CLASS}>
              {/* Page one in the printout: title, what the sheet shows, how to
                  read the colors, and the disclaimer. Page two is the grid. */}
              <div className={PRINT_INTRO_CLASS}>
                <PrintHeader className={PRINT_ONLY_CLASS}>
                  <h2>{PAGE_TITLE}</h2>
                  <p>{printSummary}</p>
                  <p>{PAGE_INTRO}</p>
                </PrintHeader>
                <ScorecardLegend
                  benchmarkColors={benchmarkColors}
                  showStandards={table.hasStandardColumns}
                />
                <Disclaimer variant="body1">{DISCLAIMER}</Disclaimer>
              </div>

              <div className={PRINT_TABLE_CLASS}>
                <ScorecardTable
                  table={table}
                  benchmarkLineByParameter={benchmarkLineByParameter}
                  periodLabel={selectedPeriod?.period_label}
                  getCellHref={getCellHref}
                  getRowHref={getRowHref}
                  onDownloadCsv={handleDownloadCsv}
                  onPrint={handlePrint}
                  emptyMessage={EMPTY_STATE}
                />
              </div>
            </PrintRoot>
          )}
        </Section>

        <Section>
          <Typography variant="h5" component="h2" gutterBottom>
            How to Read this Page
          </Typography>
          <ScorecardHowToRead />
        </Section>
      </main>
    </>
  );
}
